import { useEffect, useRef } from 'react';
import type { ZodError } from 'zod';

import type { ApiError } from '@/lib/errors/api';
import { JupiterApiError } from '@/lib/errors/jupiter';
import { reportError, shouldIgnore } from '@/lib/errors/report';

/**
 * This hook is used to report errors to both Sentry & amplitude.
 *
 * @param eventName - The name of the error event.
 * @param error - The error to report.
 */
export function useErrorReporting(
  name: string,
  error?: ApiError | JupiterApiError | ZodError<unknown> | Error,
) {
  const errorReported = useRef(false);

  useEffect(() => {
    if (error && !shouldIgnore(error) && !errorReported.current) {
      reportError(error, name);
      // ensure we only report once per unique error
      errorReported.current = true;
    }

    return () => {
      errorReported.current = false;
    };
  }, [error, name]);
}
